var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2024 Sequent Tech <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { Box, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useRecordContext } from "react-admin";
import { useTranslation } from "react-i18next";
/**
 * A styled MUI Button that looks like a link.
 * The button will have no background, no border, no padding, and an underline.
 * The text will be black and the decoration will be an underline.
 * The button will have no box shadow.
 * The button will have a pointer cursor.
 * On hover, the button will have a black color and an underline decoration.
 * On active, the button will have a primary color and no decoration.
 * On focus, the button will have a primary color and an underline decoration.
 */
var LinkButton = styled(Button)(function (_a) {
    var theme = _a.theme;
    return ({
        "minWidth": "auto",
        "minHeight": "auto",
        "background": "none",
        "fontSize": "12px",
        "color": theme.palette.primary.main,
        "border": "none",
        "padding": 0,
        "textDecoration": "none",
        "boxShadow": "none",
        "cursor": "pointer",
        /**
         * On hover, the button will have a black color and an underline decoration.
         */
        "&:hover": {
            background: "none",
            padding: 0,
            color: theme.palette.black,
            textDecoration: "underline",
            border: "none",
            boxShadow: "none",
        },
        /**
         * On active, the button will have a primary color and no decoration.
         */
        "&:active": {
            background: "none",
            padding: 0,
            color: theme.palette.primary.main,
            outline: "none",
            border: "none",
            boxShadow: "none",
        },
        /**
         * On focus, the button will have a primary color and an underline decoration.
         */
        "&:focus": {
            background: "none",
            padding: 0,
            color: theme.palette.primary.main,
            boxShadow: "none",
            border: "none",
            outline: "none",
        },
    });
});
/**
 * This component renders a string field with a show more/show less toggle.
 * The field is rendered as a text field with an ellipsis at the end when the string is longer than the initial length.
 * By default, the initial length is 256 characters.
 * When the user clicks on the field, the show more/show less toggle is displayed.
 * When the user clicks on the toggle, the field is rendered in its entirety or shortened to the initial length.
 * The toggle text is translated using the keys "electionEventScreen.common.showMore" and "electionEventScreen.common.showLess".
 * @param {MessageFieldProps} props
 * @param {string} [props.source] - The source of the data for the field if it is in the record context.
 * @param {string} [props.content] - The content of the field if is rendered directly from parent.
 * @param {number} [props.initialLength=256] - The initial length of the field.
 * @returns {ReactElement}
 */
export var MessageField = function (_a) {
    var source = _a.source, content = _a.content, _b = _a.initialLength, initialLength = _b === void 0 ? 256 : _b;
    var t = useTranslation().t;
    var base = useRecordContext();
    var _c = useState(""), data = _c[0], setData = _c[1];
    var _d = useState(false), more = _d[0], setMore = _d[1];
    useEffect(function () {
        if (base) {
            setData(content ? content : source ? base[source] : "");
        }
    }, [base, source, content]);
    return (_jsxs(Box, __assign({ sx: { width: "100%" } }, { children: [_jsx(Box, __assign({ sx: { flex: 1, overflowWrap: "anywhere" } }, { children: more ? (_jsx("span", { children: data })) : (_jsxs(_Fragment, { children: [_jsx("span", { children: data.slice(0, initialLength) }), _jsx("span", { children: data.length > initialLength ? "..." : "" })] })) })), data.length > initialLength && (_jsx(Box, __assign({ sx: { display: "flex", justifyContent: "flex-end" } }, { children: _jsx(LinkButton, __assign({ disableRipple: true, onClick: function () { return setMore(!more); } }, { children: more
                        ? t("electionEventScreen.common.showLess")
                        : t("electionEventScreen.common.showMore") })) })))] })));
};
